import React, { useState } from 'react';
import { Send, MapPin, Mail, Phone, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLang } from '@/i18n/LangContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch { setError(t.contact.error); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F3F5F7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00D186] mb-4 block">{t.contact.label}</span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight text-[#0B132B] mb-4">{t.contact.title1}{' '}<span className="text-gradient">{t.contact.title2}</span></h2>
          <p className="text-base md:text-lg text-[#475569] max-w-2xl mx-auto">{t.contact.desc}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#0B132B] rounded-xl p-8 text-white">
              <h3 className="text-xl font-bold mb-6">{t.contact.label}</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-[#00D186]" /></div>
                  <div><p className="font-medium text-sm mb-1">Ghana (HQ)</p><p className="text-white/60 text-sm">Accra - East Legon<br />Bissau Avenue (GA-334-7704)</p><p className="text-white/60 text-sm mt-1">+233 55 586 1556</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-[#0A75E5]" /></div>
                  <div><p className="font-medium text-sm mb-1">USA</p><p className="text-white/60 text-sm">15339 Keystone Bend Ln<br />Cypress, TX 77429-5961</p><p className="text-white/60 text-sm mt-1">+1 413 749 3842</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-[#0A75E5]" /></div>
                  <div><p className="font-medium text-sm mb-1">India</p><p className="text-white/60 text-sm">New Delhi, Dwarka 10</p><p className="text-white/60 text-sm mt-1">+91 870 912 3887</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-[#0A75E5]" /></div>
                  <div><p className="font-medium text-sm mb-1">Russia</p><p className="text-white/60 text-sm">Kuznetsky Most St., 21/5<br />Moscow, 107031</p><p className="text-white/60 text-sm mt-1">+7 (426) 405-91-90</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><Mail size={18} className="text-[#00D186]" /></div>
                  <div><p className="font-medium text-sm mb-1">{t.contact.email}</p><p className="text-white/60 text-sm">contact@gitnftghana.com</p></div>
                </div>
              </div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8">
              <h3 className="text-lg font-bold text-[#0B132B] mb-3">{t.contact.hours}</h3>
              <p className="text-sm text-[#475569]">{t.contact.weekdays}</p>
              <p className="text-sm text-[#475569]">{t.contact.saturday}</p>
              <p className="text-sm text-[#475569] mt-1">{t.contact.sunday}</p>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 md:p-10 shadow-sm">
              {success ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle size={48} className="text-[#00D186] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-[#0B132B] mb-2">{t.contact.success}</h3>
                  <p className="text-[#475569]">{t.contact.successDesc}</p>
                  <button onClick={() => setSuccess(false)} data-testid="contact-send-another" className="mt-6 px-6 py-2.5 bg-[#0B132B] text-white font-bold rounded-sm hover:bg-[#111C3A] transition-colors">{t.contact.sendAnother}</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div><Label htmlFor="name" className="text-[#0B132B] font-medium text-sm mb-2 block">{t.contact.name}</Label><Input id="name" name="name" value={form.name} onChange={handleChange} required data-testid="contact-input-name" placeholder={t.contact.namePh} className="border-[#E2E8F0]" /></div>
                    <div><Label htmlFor="email" className="text-[#0B132B] font-medium text-sm mb-2 block">{t.contact.emailLabel}</Label><Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required data-testid="contact-input-email" placeholder={t.contact.emailPh} className="border-[#E2E8F0]" /></div>
                  </div>
                  <div><Label htmlFor="subject" className="text-[#0B132B] font-medium text-sm mb-2 block">{t.contact.subject}</Label><Input id="subject" name="subject" value={form.subject} onChange={handleChange} data-testid="contact-input-subject" placeholder={t.contact.subjectPh} className="border-[#E2E8F0]" /></div>
                  <div><Label htmlFor="message" className="text-[#0B132B] font-medium text-sm mb-2 block">{t.contact.message}</Label><textarea id="message" name="message" value={form.message} onChange={handleChange} required rows={5} data-testid="contact-input-message" placeholder={t.contact.messagePh} className="flex w-full rounded-md border border-[#E2E8F0] bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:border-[#00D186] focus:ring-1 focus:ring-[#00D186]/20 resize-none" /></div>
                  {error && <p className="text-red-500 text-sm" data-testid="contact-error">{error}</p>}
                  <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00D186] text-[#0B132B] font-bold tracking-wide rounded-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#00D186]/20 disabled:opacity-60">{loading ? t.contact.sending : t.contact.send}<Send size={16} /></button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
